import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Edit2, Trash2, Eye, X, Mail, MessageCircle, Camera, LayoutTemplate } from 'lucide-react';
import { api } from '../lib/api';
import { Template, TemplateType } from '../types';
import { TypeBadge, ConfirmModal, Spinner, useToast, Toast } from '../components/UI';

const PH = ['{{company_name}}', '{{contact_email}}', '{{contact_phone}}', '{{category}}'];
const EMPTY_FORM = { name: '', type: 'email' as TemplateType, subject: '', body: '' };

export default function TemplatesPage(): React.ReactElement {
  const { toast, show } = useToast();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<TemplateType | 'all'>('all');
  const [modal, setModal] = useState<'add' | Template | null>(null);
  const [previewTpl, setPreviewTpl] = useState<Template | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [bodyRef, setBodyRef] = useState<HTMLTextAreaElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setTemplates(await api.getTemplates()); } catch { setTemplates([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(EMPTY_FORM); setModal('add'); };
  const openEdit = (t: Template) => { setForm({ name: t.name, type: t.type, subject: t.subject, body: t.body }); setModal(t); };

  const insertPH = (ph: string) => {
    if (!bodyRef) return;
    const start = bodyRef.selectionStart;
    const end = bodyRef.selectionEnd;
    const val = form.body;
    const newVal = val.slice(0, start) + ph + val.slice(end);
    setForm(f => ({ ...f, body: newVal }));
    setTimeout(() => { bodyRef.focus(); bodyRef.setSelectionRange(start + ph.length, start + ph.length); }, 0);
  };

  const save = async () => {
    if (!form.name.trim()) { show('Template name required', 'error'); return; }
    if (!form.body.trim()) { show('Template body required', 'error'); return; }
    setSaving(true);
    try {
      if (modal === 'add') {
        const created = await api.createTemplate(form);
        setTemplates(prev => [created, ...prev]);
        show(`"${form.name}" created`, 'success');
      } else if (modal && typeof modal === 'object') {
        const updated = await api.updateTemplate(modal.id, form);
        setTemplates(prev => prev.map(t => t.id === updated.id ? updated : t));
        show('Template updated', 'success');
      }
      setModal(null);
    } catch (e: any) { show(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await api.deleteTemplate(deleteTarget.id).catch(() => null);
    setTemplates(prev => prev.filter(t => t.id !== deleteTarget.id));
    show('Template deleted', 'info'); setDeleteTarget(null);
  };

  const fld = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm(p => ({ ...p, [k]: e.target.value }));

  const typeIcon = (type: TemplateType) => {
    if (type === 'email') return <Mail size={14} />;
    if (type === 'whatsapp') return <MessageCircle size={14} />;
    return <Camera size={14} />;
  };
  const typeColor = (type: TemplateType) => ({ email: 'var(--accent)', whatsapp: 'var(--accent3)', instagram: 'var(--accent2)' }[type]);

  const filtered = typeFilter === 'all' ? templates : templates.filter(t => t.type === typeFilter);

  return (
    <div className="page fade-in">
      {toast && <Toast msg={toast.msg} type={toast.type} />}

      <div className="page-header">
        <div>
          <h1 className="page-title">Templates</h1>
          <p className="page-sub">{templates.length} template{templates.length !== 1 ? 's' : ''} — use placeholders to personalise</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}><Plus size={15} /> New Template</button>
      </div>

      {/* Type filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {(['all', 'email', 'whatsapp', 'instagram'] as const).map(t => (
          <button key={t} className={`chip ${typeFilter === t ? 'active' : ''}`} onClick={() => setTypeFilter(t)}>
            {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <LayoutTemplate size={32} />
            <h3>{typeFilter !== 'all' ? 'No templates of this type' : 'No templates yet'}</h3>
            <p>Create your first template with dynamic placeholders to personalise messages at scale.</p>
            <button className="btn btn-primary btn-sm" onClick={openAdd}><Plus size={13} /> New Template</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {filtered.map(t => (
            <div key={t.id} className="card" style={{ borderLeft: `3px solid ${typeColor(t.type)}`, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                  <TypeBadge type={t.type} />
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPreviewTpl(t)} title="Preview"><Eye size={12} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}><Edit2 size={12} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(t)}><Trash2 size={12} /></button>
                </div>
              </div>
              {t.subject && <div style={{ fontSize: 12, color: 'var(--text2)', borderTop: '1px solid var(--border)', paddingTop: 10 }}><strong style={{ color: 'var(--text3)', textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px' }}>Subject</strong><br />{t.subject}</div>}
              <div style={{ fontSize: 12, color: 'var(--text3)', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any }}>{t.body}</div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div className="modal modal-wide">
            <div className="modal-header">
              <h3>{modal === 'add' ? 'New Template' : `Edit — ${(modal as Template).name}`}</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setModal(null)}><X size={14} /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Template Name *</label>
                  <input placeholder="e.g. Cold Outreach Email" value={form.name} onChange={fld('name')} autoFocus />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select value={form.type} onChange={fld('type')}>
                    <option value="email">📧 Email</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="instagram">📸 Instagram</option>
                  </select>
                </div>
              </div>
              {form.type === 'email' && (
                <div className="form-group">
                  <label>Subject Line</label>
                  <input placeholder="Hi {{company_name}}, let's chat" value={form.subject} onChange={fld('subject')} />
                </div>
              )}
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Message Body *</label>
                <textarea
                  ref={setBodyRef}
                  placeholder="Hi {{company_name}},&#10;&#10;I came across your work and wanted to reach out…"
                  value={form.body} onChange={fld('body')}
                  style={{ minHeight: 160, fontFamily: 'DM Sans, sans-serif' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                <span style={{ fontSize: 11, color: 'var(--text3)', alignSelf: 'center' }}>Insert placeholder:</span>
                {PH.map(ph => (
                  <button key={ph} className="btn btn-ghost btn-sm" style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }} onClick={() => insertPH(ph)}>{ph}</button>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <Spinner size={14} /> : null}
                {modal === 'add' ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewTpl && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setPreviewTpl(null)}>
          <div className="modal modal-wide">
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: typeColor(previewTpl.type) }}>{typeIcon(previewTpl.type)}</span>
                <h3>{previewTpl.name}</h3>
                <TypeBadge type={previewTpl.type} />
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setPreviewTpl(null)}><X size={14} /></button>
            </div>
            <div className="modal-body">
              {previewTpl.subject && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Subject</div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{previewTpl.subject}</div>
                </div>
              )}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Body</div>
              <div className="preview-box" style={{ whiteSpace: 'pre-wrap' }}>{previewTpl.body}</div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Template"
          message={<>Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.</>}
          onConfirm={confirmDelete} onCancel={() => setDeleteTarget(null)} danger
        />
      )}
    </div>
  );
}
